import React, { useEffect, useMemo, useState, memo } from "react";
import "./Navigation.css";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import APIService from "../../Service/APIService";

import TissueVaultLogo from "../../icons/tissue-vault-logo.js";
import TissueVaultCollapsed from "../../icons/default-logo-collapsed.js";

import Button from "../../common/button/button.js";
import Divider from "../../common/divider/divider.js";
import ModalView from "../../common/modal/index.js";
import NewDirectOrderModal from "../DirectOrder/NewDirectOrderModal.js";

import PlusIconOutlined from "../../icons/plus-icon-outlined.js";
import DashboardIcon from "../../icons/dashboard-icon.js";
import AccountsIcon from "../../icons/accounts-icon.js";
import ApprovalsIcon from "../../icons/approvals-icon.js";
import InvoicesIcon from "../../icons/invoices-icon.js";
import OrderIcon from "../../icons/order-icon.js";
import DocumentOutlined from "../../icons/document-outlined.js";
import DistributorIcon from "../../icons/distributor-icon.js";
import PaymentIcon from "../../icons/payment-icon.js";
import ManufacturerIcon from "../../icons/manufacturer-icon.js";
import AsUsersIcon from "../../icons/as-users-icon.js";
import UsersIcon from "../../icons/users-icon.js";
import LinkIcon from "../../icons/link-icon.js";
import ReportsIcon from "../../icons/reports-icon.js";

import { useScreen } from "../Utils/Screens.js";
import {
  canAccessConsignmentOrderActions,
  canAccessDirectOrderActions,
  canAccessNewDirectOrder,
  canSubmitIvr,
  canViewIvrDashboard,
} from "../Utils/accessControl";
import { canAccessCommissionsPage } from "../CommissionStatements/commissionPageAccess.js";
import { hasPermission } from "../../Config/permissions.js";

const Navigation = ({
  userType,
  userData = {},
  closeSidebar,
  toggleSidebar,
  setClosePhoneSidebar,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const isPhone = !useScreen("sm");

  const [modalVisible, setModalVisible] = useState(false);
  const [isChildAccUser, setIsChildAccUser] = useState(false);
  const [isDirectOrder, setIsDirectOrder] = useState(false);

  const closeModal = () => setModalVisible(false);
  const openModal = () => setModalVisible(true);
  const canShowConsignmentOrderActions =
    canAccessConsignmentOrderActions(userData);

  const handleNavigate = (path) => {
    closeModal();
    if (isPhone) setClosePhoneSidebar?.(false);
    navigate(path);
  };

  const handleDirectOrder = () => {
    closeModal();
    setIsDirectOrder(true);
  };

  useEffect(() => {
    const fetchAccount = async () => {
      if (userType !== "Account" || !userData?.AccountID) return;

      try {
        const api = new APIService();
        const response = await api.getDataFromBackend(
          `getaccountbyid?ID=${userData.AccountID}`
        );

        const acc = response?.data?.data;

        setIsChildAccUser(!!acc?.IsChildAccount);
      } catch (error) {
        console.error("Sidebar account fetch failed:", error);
      }
    };

    fetchAccount();
  }, [userType, userData]);

  const mainMenuItems = useMemo(() => {
    const items = [
      {
        path: "/",
        label: "Dashboard",
        icon: <DashboardIcon />,
      },
      ...(userType === "Admin" || userType === "Authorizer"
        ? [
          {
            path: "/ApprovalsDashboard",
            label: "Approvals",
            icon: <ApprovalsIcon />,
          },
        ]
        : []),
      // {
      //   path: "/IVRSubmissions",
      //   label: "IVR Submissions",
      //   icon: <DocumentOutlined />,
      // },
      ...(canViewIvrDashboard(userData)
        ? [
          {
            path: "/IVRDashboard",
            label: "IVRs",
            icon: <DocumentOutlined />,
          },
        ]
        : []),
      {
        path: "/Orders",
        label: "Orders",
        icon: <OrderIcon />,
      },
      {
        path: "/InvoiceDashboard",
        label: "Invoices",
        icon: <InvoicesIcon />,
      },
      ...(!isChildAccUser
        ? [
          {
            path: "/AccountDashboard",
            label: "Accounts",
            icon: <AccountsIcon />,
          },
        ]
        : []),
    ];

    return items;
  }, [userType, userData, isChildAccUser]);

  const adminMenuItems = useMemo(() => {
    if (
      !["Admin", "ASD", "Authorizer", "Viewer"].includes(userType)
    ) {
      return [];
    }

    return [
      ...(canAccessCommissionsPage(userData)
        ? [
          {
            path: "/CommissionStatements",
            label: "Commissions",
            icon: <PaymentIcon />,
          },
        ]
        : []),
      {
        path: "/DistributorDashboard",
        label: "Distributors",
        icon: <DistributorIcon />,
      },
      {
        path: "/PaymentsDashboard",
        label: "Payments",
        icon: <PaymentIcon />,
      },
      {
        path: "/ManufacturerDashboard",
        label: "Manufacturers",
        icon: <ManufacturerIcon />,
      },
      ...(hasPermission(userData, "user.view")
        ? [
          {
            path: "/Users",
            label: "Users",
            icon: <UsersIcon />,
          },
        ]
        : []),
      ...(userData?.IsSuperAdmin
        ? [
          {
            path: "/AdminTools",
            label: "Admin Tools",
            icon: <AsUsersIcon />,
          },
        ]
        : []),
    ];
  }, [userType, userData]);

  const renderMenuItem = ({ path, label, icon }) => (
    <NavLink key={path} to={path} className="nav-link">
      <Button
        variant={closeSidebar ? "icon" : "ghost"}
        icon={icon}
        isActive={location.pathname === path}
      >
        {!closeSidebar && label}
      </Button>
    </NavLink>
  );

  return (
    <div className={isPhone ? "sidebar-phone-container" : "sidebar"}>
      <div className="sidebar-top">
        <div className="sidebar-top-sticky">
          {!isPhone && (
            <div>
              {closeSidebar ? (
                <TissueVaultCollapsed />
              ) : (
                <TissueVaultLogo />
              )}
            </div>
          )}

          {!isPhone && (
            <button
              onClick={toggleSidebar}
              className={`sidebar-toggle ${closeSidebar ? "collapsed" : "expanded"
                }`}
            >
              <img
                src={
                  closeSidebar
                    ? "/right-arrow.png"
                    : "/left-arrow.png"
                }
                alt="Toggle Sidebar"
              />
            </button>
          )}

          <Button
            variant={closeSidebar ? "icon" : "primary"}
            onClick={openModal}
            className="side-bar-button-new"
            icon={<PlusIconOutlined />}
          >
            {!closeSidebar && "New"}
          </Button>

          <Divider />
        </div>

        <div className="sidebar-menu-scrollable">
          <div className="sidebar-menu">
            {mainMenuItems.map(renderMenuItem)}
          </div>

          {userData?.Reports && (
            <>
              <Divider />
              {renderMenuItem({
                path: "/ReportsDashboard",
                label: "Reports",
                icon: <ReportsIcon />,
              })}
            </>
          )}

          {adminMenuItems.length > 0 && (
            <>
              <Divider />
              <div className="sidebar-menu">
                {adminMenuItems.map(renderMenuItem)}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="sidebar-bottom-container">
        <div className="sidebar-bottom">
          <a
            href="https://netorg12800691.sharepoint.com/:f:/s/1099Distributors/IgDFATN55eVpQp56-foDLPqoAa5g8t2Ka8mzyXYCBBKmCdE?e=HuS10K"
            target="_blank"
            rel="noopener noreferrer"
            className="nav-link"
          >
            <Button
              variant={closeSidebar ? "icon" : "ghost"}
              icon={<LinkIcon />}
            >
              {!closeSidebar && "Marketing Materials"}
            </Button>
          </a>

          <a
            href="https://www.tissuevault.health/support"
            target="_blank"
            rel="noopener noreferrer"
            className="nav-link"
          >
            <Button
              variant={closeSidebar ? "icon" : "ghost"}
              icon={<LinkIcon />}
            >
              {!closeSidebar && "Get Support"}
            </Button>
          </a>

          <a
            href="https://www.tissuevault.health/status"
            target="_blank"
            rel="noopener noreferrer"
            className="nav-link"
          >
            <Button
              variant={closeSidebar ? "icon" : "ghost"}
              icon={<LinkIcon />}
            >
              {!closeSidebar && "Check App Status"}
            </Button>
          </a>
        </div>
      </div>

      <ModalView
        isOpen={modalVisible}
        minWidth="360px"
        onClose={closeModal}
        modalTitle="New"
        textInputComponent={
          <div className="new-actions-modal">
            {canAccessNewDirectOrder(userData) &&
              <Button
                onClick={handleDirectOrder}
                type="primary"
                icon={<PlusIconOutlined />}
              >
                Direct Order (New Version)
              </Button>
            }
            {canAccessDirectOrderActions(userData) && (
              <Button
                onClick={() =>
                  handleNavigate("/DirectOrder")
                }
                type="primary"
                icon={<PlusIconOutlined />}
              >
                Direct Order
              </Button>
            )}
            {canShowConsignmentOrderActions && (
              <>
                <Button
                  onClick={() =>
                    handleNavigate("/ConsignmentOrder")
                  }
                  type="primary"
                  icon={<PlusIconOutlined />}
                >
                  Consignment Order
                </Button>

                <Button
                  onClick={() =>
                    handleNavigate("/ProductUsage")
                  }
                  type="primary"
                  icon={<PlusIconOutlined />}
                >
                  Product Usage
                </Button>
              </>
            )}

            {canSubmitIvr(userData) && (
              <Button
                onClick={() => handleNavigate("/NewForm")}
                type="primary"
                icon={<PlusIconOutlined />}
              >
                Submit IVR
              </Button>
            )}
          </div>
        }
      />

      <NewDirectOrderModal
        isDirectOrder={isDirectOrder}
        setIsDirectOrder={setIsDirectOrder}
      />
    </div>
  );
};

export default memo(Navigation);
